// Core packages

// Third party packages

// Custom packages
import {
  differenceLabels,
  findSameCodeDifferences,
  SameCodeChoice,
  SameCodeFormField,
  SameCodeLockReason,
} from "src/app/modules/properties/same-code-docs-edit.utils";
import {
  ARCHIVE_PAPER_SERVER_OWNED_DATA_KEYS,
  ARCHIVE_SERVER_OWNED_DATA_KEYS,
  ARCHIVE_UPDATE_MAX_ADD_FILES,
  ARCHIVE_UPDATE_MAX_DOCS,
  ARCHIVE_UPDATE_MAX_REMOVE_FILES,
  DocumentArchiveDocUpdateDocsPayload,
  DocumentArchiveTreeArea,
  DocumentArchiveTreeContent,
  DocumentArchiveTreeDoc,
  DocumentArchiveTreeGroup,
  DocumentArchiveTreeRow,
} from "src/app/shared/interfaces/document-archive-doc.interface";

/**
 * Pure helpers of "Modifica dati aggiuntivi" on the 3.0.0 archive page (`documentsV3`): which rows
 * the save can be written on, what the user changed in the files, and the body of
 * `POST /document-archive-docs/update-docs`.
 *
 * Kept out of the page and of the dialog so the rules can be tested without Angular, and so the two
 * cannot disagree on them.
 *
 * @since 3.0.0
 */

/** What `documentCode` ends with on a paper document ("000071 C"): the back-end rule, verbatim */
const PAPER_CODE_SUFFIX = " C";

/**
 * Keys never copied out of a model: assigning them would reach the prototype of the copy. The
 * back-end refuses them anyway, so nothing legitimate is lost.
 */
const UNSAFE_KEYS = ["__proto__", "constructor", "prototype"];

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Trimmed text of a string, "" for anything else */
function textOf(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** The copy of a contract served inside the aggregated contracts section */
function isAggregatedCopy(row: DocumentArchiveTreeRow): boolean {
  return typeof row.executorName === "string";
}

/** The data form of a document type, "" when it has none */
function dataTypeIdOf(templateDoc: DocumentArchiveTreeDoc): string {
  return templateDoc && typeof templateDoc.dataTypeId === "string" ? templateDoc.dataTypeId : "";
}

/**
 * Whether a row is a PAPER document, whose files cannot be changed from the dialog (the back-end
 * answers 400).
 *
 * Read from `documentCode`, the "Codice" column the back-end computes, with exactly the test the
 * back-end applies: a row shown as "000071 C" is a row the back-end treats as paper. A row whose
 * barcode has no recognised shape has a `null` code and is NOT paper.
 *
 * @param row The row, as the tree serves it
 */
export function isPaperArchiveRow(row: { documentCode?: string | null } | null | undefined): boolean {
  const code = row && typeof row.documentCode === "string" ? row.documentCode : "";
  return (
    code.length > PAPER_CODE_SUFFIX.length &&
    code.slice(-PAPER_CODE_SUFFIX.length) === PAPER_CODE_SUFFIX
  );
}

/**
 * One file attached to a row, as the dialog lists it.
 *
 * @since 3.0.0
 */
export interface ArchiveStoredFile {
  /**
   * Storage name, e.g. `9f8e7d6c-….pdf`: the ONLY identifier of a file. It is what the download
   * asks for and what the save detaches — never the position, never the path.
   */
  filename: string;
  /** The name to show: the original upload name, or the storage name when none was recorded */
  originalName: string;
  /** Bytes as stored, `0` when not recorded */
  size: number;
}

/**
 * The files of a row, out of `data.document[]`, in stored order.
 *
 * An entry without a `filename` is left out: nothing can address it, so it could be neither
 * downloaded nor detached. `data.documentFile[]` is NOT read: it is a legacy residue the back-end
 * no longer considers. A storage name met twice is listed once, as the back-end serves the first.
 *
 * @param data `data` of the row
 */
export function archiveStoredFiles(data: unknown): ArchiveStoredFile[] {
  const list = isRecord(data) ? data.document : undefined;
  const entries: unknown[] = Array.isArray(list) ? list : [];
  const files: ArchiveStoredFile[] = [];
  const seen: { [filename: string]: boolean } = Object.create(null);

  entries.forEach((entry: unknown) => {
    if (!isRecord(entry)) {
      return;
    }
    const filename = textOf(entry.filename);
    if (filename === "" || seen[filename]) {
      return;
    }
    seen[filename] = true;
    const originalName = textOf(entry.originalName);
    const rawSize = entry.size;
    const size = typeof rawSize === "number" && isFinite(rawSize) && rawSize > 0 ? rawSize : 0;
    files.push({
      filename,
      originalName: originalName !== "" ? originalName : filename,
      size,
    });
  });

  return files;
}

/**
 * A row of the archive tree together with where it sits.
 *
 * @since 3.0.0
 */
export interface ArchiveSameCodeRow {
  row: DocumentArchiveTreeRow;
  /** The document type the row is filed under */
  templateDoc: DocumentArchiveTreeDoc;
  area: DocumentArchiveTreeArea;
  content: DocumentArchiveTreeContent;
  group: DocumentArchiveTreeGroup;
}

/**
 * Every row of the tree filed under a barcode: the rows of ONE archived document, which the save can
 * be written on together.
 *
 * - The barcode is compared EXACTLY, as the back-end compares it: update-docs refuses rows with
 *   different codes.
 * - Each row comes ONCE. A contract is served twice, under its own area and inside the aggregated
 *   contracts section, with the same `_id`: the copy in its own area is kept, because its place
 *   names the element it hangs from (the aggregated copy is the one carrying `executorName`).
 * - Deleted rows (the ones revealed by "Mostra strutture eliminate") and mockups are left out: the
 *   back-end refuses them.
 *
 * The tree is pruned, so a row filed under a document type retired from the taxonomy is not in it
 * and cannot be offered: the same limit as the delete dialog of the page.
 *
 * @param areas   The tree, as the page holds it
 * @param barCode `data.barCode` of the clicked row
 */
export function collectArchiveSameCodeRows(
  areas: DocumentArchiveTreeArea[],
  barCode: string
): ArchiveSameCodeRow[] {
  if (typeof barCode !== "string" || barCode === "") {
    return [];
  }

  const rows: ArchiveSameCodeRow[] = [];
  const positionOfId: { [docId: string]: number } = Object.create(null);

  (Array.isArray(areas) ? areas : []).forEach((area) => {
    const contents = area && Array.isArray(area.content) ? area.content : [];
    contents.forEach((content) => {
      const groups = content && Array.isArray(content.groups) ? content.groups : [];
      groups.forEach((group) => {
        const types = group && Array.isArray(group.documents) ? group.documents : [];
        types.forEach((templateDoc) => {
          const archiveDocs =
            templateDoc && Array.isArray(templateDoc.archiveDocs) ? templateDoc.archiveDocs : [];
          archiveDocs.forEach((row) => {
            if (
              !row ||
              !row._id ||
              row.deleted === true ||
              row.mockup === true ||
              !row.data ||
              row.data.barCode !== barCode
            ) {
              return;
            }

            const entry: ArchiveSameCodeRow = { row, templateDoc, area, content, group };
            const position = positionOfId[row._id];
            if (position === undefined) {
              positionOfId[row._id] = rows.length;
              rows.push(entry);
              return;
            }
            const kept = rows[position];
            if (kept && isAggregatedCopy(kept.row) && !isAggregatedCopy(row)) {
              rows[position] = entry;
            }
          });
        });
      });
    });
  });

  return rows;
}

/**
 * The labels of the place of a row, localized by the caller.
 *
 * @since 3.0.0
 */
export interface ArchiveRowPlace {
  area: string;
  section: string;
  /** The block (mapping element, conductor, ...), "" for the plain group of a section */
  block: string;
}

/**
 * What `buildArchiveSameCodeChoices` needs.
 *
 * @since 3.0.0
 */
export interface ArchiveSameCodeChoicesInput {
  /** The rows of the document, see `collectArchiveSameCodeRows` */
  rows: ArchiveSameCodeRow[];
  /** `_id` of the row the edit was opened from: the form shows its data */
  clickedDocId: string;
  /** Fields of the form of the clicked row, see `collectSameCodeFormFields` */
  fields: SameCodeFormField[];
  /** Localized title of a document type */
  titleOf: (templateDoc: DocumentArchiveTreeDoc) => string;
  /** Localized place of a row */
  placeOf: (entry: ArchiveSameCodeRow) => ArchiveRowPlace;
}

/**
 * The rows of the selection dialog (`PropertySameCodeEditDialogComponent`, shared with the legacy
 * page), in tree order, or null when the clicked row is not among `rows`.
 *
 * Nothing is ticked: the choice is the user's. A row that is not the clicked one is LOCKED when
 * saving the form on it would write data the user never saw:
 *  - `differentForm`: its document type uses another data form (`dataTypeId`). The back-end refuses
 *    that mix as well when the form is sent;
 *  - `differentData`: the values of the form differ from the clicked row's. Only the fields of the
 *    form are compared — not the barcode, not the file lists — with the rules of the legacy
 *    selection (empty and missing are equal, dates by day, Formly defaults applied).
 *
 * @param input The rows, the clicked one, its form and the labels
 */
export function buildArchiveSameCodeChoices(
  input: ArchiveSameCodeChoicesInput
): SameCodeChoice[] | null {
  const rows = input && Array.isArray(input.rows) ? input.rows : [];
  const clicked = rows.filter((entry) => entry.row._id === input.clickedDocId)[0];
  if (!clicked) {
    return null;
  }
  const clickedForm = dataTypeIdOf(clicked.templateDoc);
  const fields = Array.isArray(input.fields) ? input.fields : [];

  return rows.map((entry) => {
    const isClicked = entry === clicked;
    let lockReason: SameCodeLockReason | null = null;
    let differences: { labels: string[]; unlabeled: boolean } = { labels: [], unlabeled: false };

    if (!isClicked) {
      if (dataTypeIdOf(entry.templateDoc) !== clickedForm) {
        lockReason = "differentForm";
      } else {
        const different = findSameCodeDifferences(fields, clicked.row.data, entry.row.data);
        if (different.length) {
          lockReason = "differentData";
          differences = differenceLabels(different, fields);
        }
      }
    }

    const place = input.placeOf(entry);
    const parts = [textOf(place.area), textOf(place.section), textOf(place.block)];
    let last = -1;
    parts.forEach((part, index) => {
      if (part !== "") {
        last = index;
      }
    });
    // "Area > Section > Block", split over the three slots the dialog prints side by side
    const slot = (index: number): string =>
      parts[index] === "" ? "" : index < last ? `${parts[index]} >` : parts[index];

    return {
      subDocId: entry.row._id,
      key: entry.templateDoc && entry.templateDoc._id ? entry.templateDoc._id : "",
      structureObjectId: textOf(entry.row.structureObjectId),
      docTitle: input.titleOf(entry.templateDoc),
      area: slot(0),
      subArea: slot(1),
      structureObjectName: slot(2),
      isClicked,
      lockReason,
      differentFields: differences.labels,
      differentUnlabeled: differences.unlabeled,
    };
  });
}

/**
 * The rows a selection writes: the ticked rows that are not locked, each once, at most
 * `ARCHIVE_UPDATE_MAX_DOCS` — or null when there are more, which the back-end would refuse.
 *
 * @param selected Rows ticked in the selection dialog
 */
export function archiveSelectedDocIds(selected: SameCodeChoice[]): string[] | null {
  const ids: string[] = [];
  (Array.isArray(selected) ? selected : []).forEach((choice) => {
    if (choice && choice.lockReason === null && choice.subDocId && ids.indexOf(choice.subDocId) === -1) {
      ids.push(choice.subDocId);
    }
  });
  return ids.length > ARCHIVE_UPDATE_MAX_DOCS ? null : ids;
}

/**
 * The form as update-docs takes it: a shallow copy of the model WITHOUT the keys the server owns.
 *
 * The model is the whole `data` of the clicked row, form fields and the rest, exactly as the PUT used
 * to receive it: the back-end writes it as the new form and removes the keys it no longer holds, so
 * dropping the non form keys here would delete them. Only the server's keys are left out — the file
 * lists, the barcode and who filed the document and when, plus the parts and the folder code on a
 * paper document — because the back-end ignores them, and a long file list could break the 300 keys
 * limit of the form.
 *
 * @param model The model of the dialog
 * @param paper Whether the row is a paper document
 */
export function archiveUpdateData(model: unknown, paper: boolean): { [key: string]: any } {
  const owned = paper
    ? ARCHIVE_SERVER_OWNED_DATA_KEYS.concat(ARCHIVE_PAPER_SERVER_OWNED_DATA_KEYS)
    : ARCHIVE_SERVER_OWNED_DATA_KEYS;
  const data: { [key: string]: any } = {};
  if (!isRecord(model)) {
    return data;
  }
  Object.keys(model).forEach((key) => {
    if (owned.indexOf(key) > -1 || UNSAFE_KEYS.indexOf(key) > -1) {
      return;
    }
    data[key] = model[key];
  });
  return data;
}

/**
 * The upload ids of what the uploader holds, in order and each once.
 *
 * Takes both shapes `POST /api/v2/files` produces — the objects carrying `_id` and the bare ids —
 * and drops anything else: an entry with no id cannot be attached.
 *
 * @param uploads The value of the uploader field
 */
export function archiveUploadIds(uploads: unknown): string[] {
  const ids: string[] = [];
  const seen: { [id: string]: boolean } = Object.create(null);
  (Array.isArray(uploads) ? uploads : []).forEach((upload: unknown) => {
    const id = isRecord(upload) ? textOf(upload._id) : textOf(upload);
    const normalized = id.toLowerCase();
    if (id === "" || seen[normalized]) {
      return;
    }
    seen[normalized] = true;
    ids.push(id);
  });
  return ids;
}

/**
 * What the user changed in the files of the dialog, not yet sent.
 *
 * @since 3.0.0
 */
export interface ArchiveFileChanges {
  /** Upload ids from `POST /api/v2/files`, in the order they were added */
  addFiles: string[];
  /** `filename` of the stored entries to detach */
  removeFiles: string[];
}

/**
 * Whether a file change goes over a limit of update-docs, which would answer 400.
 *
 * @param changes The pending file changes
 */
export function archiveFileChangesOverLimit(changes: ArchiveFileChanges): boolean {
  return (
    !!changes &&
    ((Array.isArray(changes.addFiles) && changes.addFiles.length > ARCHIVE_UPDATE_MAX_ADD_FILES) ||
      (Array.isArray(changes.removeFiles) &&
        changes.removeFiles.length > ARCHIVE_UPDATE_MAX_REMOVE_FILES))
  );
}

/**
 * The body of update-docs, or null when there is nothing to send.
 *
 * Each part is sent only when it holds something: `data` when the form is to be written, the two
 * file lists when not empty. With none of the three the back-end answers 400, so the caller says
 * "nothing to save" instead of calling.
 *
 * @param input The archive, the rows, the form (null to leave it untouched) and the file changes
 */
export function buildArchiveUpdatePayload(input: {
  documentArchiveId: string;
  docIds: string[];
  data: { [key: string]: any } | null;
  changes: ArchiveFileChanges;
}): DocumentArchiveDocUpdateDocsPayload | null {
  const documentArchiveId = textOf(input.documentArchiveId);
  const docIds = Array.isArray(input.docIds) ? input.docIds.filter((id) => textOf(id) !== "") : [];
  if (documentArchiveId === "" || !docIds.length) {
    return null;
  }

  const addFiles =
    input.changes && Array.isArray(input.changes.addFiles) ? input.changes.addFiles : [];
  const removeFiles =
    input.changes && Array.isArray(input.changes.removeFiles) ? input.changes.removeFiles : [];
  const hasData = isRecord(input.data);
  if (!hasData && !addFiles.length && !removeFiles.length) {
    return null;
  }

  const payload: DocumentArchiveDocUpdateDocsPayload = { documentArchiveId, docIds };
  const data = input.data;
  if (isRecord(data)) {
    payload.data = data;
  }
  if (addFiles.length) {
    payload.addFiles = addFiles.slice();
  }
  if (removeFiles.length) {
    payload.removeFiles = removeFiles.slice();
  }
  return payload;
}

/**
 * Whether the report of update-docs says a row changed between the checks and the write: the user
 * is told, and the tree is reloaded. The other two reasons (`already_attached`, `not_attached`) are
 * the expected outcome of a repeated call or of a file some rows did not hold.
 *
 * @param result `data` of the 200
 */
export function archiveUpdateChangedMeanwhile(result: unknown): boolean {
  const list = isRecord(result) ? result.skipped : undefined;
  const skipped: unknown[] = Array.isArray(list) ? list : [];
  return skipped.some(
    (entry: unknown) => isRecord(entry) && entry.reason === "changed_meanwhile"
  );
}

/**
 * How a refused save is reported. The back-end messages are in English, so each case gets its own
 * text; a 401 goes through the handling every other page uses.
 *
 * @since 3.0.0
 */
export type ArchiveUpdateErrorKind =
  | "invalid"
  | "session"
  | "forbidden"
  | "notFound"
  | "gone"
  | "unknown";

/**
 * The kind of a failed update-docs call, from its HTTP status.
 *
 * `notFound` asks for a reload — the archive or a row is gone or not visible — and `gone` for the
 * uploads to be repeated. A failure without status (network down) is `unknown`.
 *
 * @param err The error the call failed with
 */
export function archiveUpdateErrorKind(err: unknown): ArchiveUpdateErrorKind {
  const rawStatus = isRecord(err) ? err.status : undefined;
  const status = typeof rawStatus === "number" ? rawStatus : 0;
  switch (status) {
    case 400:
      return "invalid";
    case 401:
      return "session";
    case 403:
      return "forbidden";
    case 404:
      return "notFound";
    case 410:
      return "gone";
    default:
      return "unknown";
  }
}
